import { useEffect, useState } from "react";
import { WorkingAvatar } from "../WorkingAvatar";
import {
  TRAINING_PHASES,
  TRAINING_PHASE_COUNT,
  isTrainingRunStale,
  trainingPhaseLabelKey,
  trainingPhaseTypicalS,
  trainingResumeAllowed,
  trainingResumeState,
  trainingRunView,
  type TrainingPhaseDto,
  type TrainingRunDto,
  type TrainingView
} from "../../shared/training-run";
import { staffStepStatus } from "./core";
import { TrainingPhaseCard } from "./PhaseCards";
import { parseValue, type StaffPaneProps } from "./console-types";
import "./training-run.css";

/**
 * Live training visibility (T9, Pavel 2026-08-05): the partner watches the
 * Instance Creator run with the same detail the hub gives - phase ladder,
 * live elapsed, failures with the runner's own words, silence as a warning,
 * and a resume control - all in Launchpad grammar, no hub UI copied.
 *
 * The mode adopted from the Instance Creator is its PhaseTimeline + curated
 * status layer (the raw terminal needed 100+ hide-rules to be readable, which
 * is proof logs are not a human surface). The hub detail stays the deep-debug
 * escape hatch via the instance card next to this one.
 */

function formatSeconds(total: number): string {
  const seconds = Math.max(0, Math.round(total));
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  if (minutes < 60) return rest > 0 ? `${minutes} min ${rest} s` : `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
}

function formatClock(ts: string): string {
  const parsed = new Date(ts);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });
}

/** 1 Hz tick while anything on the card is live; anchored math, no drift. */
function useNowTick(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return now;
}

interface LadderRow {
  phase: number;
  reported: TrainingPhaseDto | null;
  state: "done" | "running" | "failed" | "pending";
}

export function trainingLadder(run: TrainingRunDto): LadderRow[] {
  const byNumber = new Map(run.phases.map((phase) => [phase.phase, phase]));
  const numbers = new Set<number>(TRAINING_PHASES.map((entry) => entry.phase));
  for (const phase of run.phases) numbers.add(phase.phase);
  return [...numbers].sort((a, b) => a - b).map((phase) => {
    const reported = byNumber.get(phase) ?? null;
    const state = reported
      ? reported.status === "done" ? "done" : reported.status === "failed" ? "failed" : "running"
      : "pending";
    return { phase, reported, state };
  });
}

function PhaseLadder(props: {
  run: TrainingRunDto;
  live: boolean;
  now: number;
  t: (key: string) => string;
}) {
  const rows = trainingLadder(props.run);
  return (
    <ol className="training-run-phases">
      {rows.map((row) => {
        const labelKey = trainingPhaseLabelKey(row.phase);
        const label = labelKey ? props.t(labelKey) : `${row.phase}.`;
        const typical = trainingPhaseTypicalS(row.phase);
        const running = row.state === "running" && props.live;
        const elapsed = row.reported && running
          ? Math.max(0, Math.floor((props.now - Date.parse(row.reported.startedAt)) / 1000))
          : null;
        return (
          <li className={`training-phase is-${row.state}`} key={row.phase} data-training-phase={row.phase}>
            <span className="training-phase-marker" aria-hidden="true">
              {row.state === "done" && <span className="training-phase-check">✓</span>}
              {running && <WorkingAvatar size="rail" className="training-phase-working" />}
              {row.state === "failed" && <span className="training-phase-cross">!</span>}
              {(row.state === "pending" || (row.state === "running" && !props.live)) && (
                <span className="training-phase-dot" />
              )}
            </span>
            <span className="training-phase-body">
              <span className="training-phase-name">
                <span className="training-phase-number">{row.phase}.</span> {label}
              </span>
              {row.state === "done" && row.reported?.durationS != null && (
                <span className="training-phase-note">{formatSeconds(row.reported.durationS)}</span>
              )}
              {running && elapsed !== null && (
                <span className="training-phase-note training-phase-live">
                  {props.t("staffConsole.trainingRun.elapsed").replace("{duration}", formatSeconds(elapsed))}
                  {typical !== null && (
                    <span className="training-phase-typical">
                      {" "}{props.t("staffConsole.trainingRun.typical").replace("{minutes}", String(Math.max(1, Math.ceil(typical / 60))))}
                    </span>
                  )}
                </span>
              )}
              {running && row.reported?.detail && (
                <span className="training-phase-note">{row.reported.detail}</span>
              )}
              {row.state === "failed" && (
                <span className="training-phase-note training-phase-error">
                  {row.reported?.error || props.t("staffConsole.trainingRun.phaseFailed")}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function ActivityFeed(props: { run: TrainingRunDto; t: (key: string) => string }) {
  if (props.run.events.length === 0) return null;
  return (
    <details className="training-run-activity">
      <summary>{props.t("staffConsole.trainingRun.activity")}</summary>
      <ul>
        {[...props.run.events].reverse().map((event, index) => (
          <li className={`training-event is-${event.kind}`} key={`${event.ts}-${index}`}>
            <span className="training-event-time">{formatClock(event.ts)}</span>
            <span className="training-event-text">{event.text}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function ResumeControl(props: StaffPaneProps & { view: TrainingView; now: number }) {
  const [phase, setPhase] = useState<"idle" | "confirm" | "sending" | "sent">("idle");
  const [refusal, setRefusal] = useState<"run_active" | "not_ready" | "error" | null>(null);
  const resumeState = trainingResumeState(
    props.detail.trainingRun ?? null,
    props.detail.trainingResumeRequestedAt ?? null,
    props.now
  );
  // A pending request replaces the button entirely: firing twice cannot help.
  if (phase === "sent" || resumeState === "requested") {
    return <p className="message training-resume-note">{props.t("staffConsole.trainingRun.resumeRequested")}</p>;
  }
  if (!trainingResumeAllowed(props.view)) return null;
  async function send() {
    setPhase("sending");
    setRefusal(null);
    const response = await props.run(
      `/api/staff/clients/${props.detail.client.id}/training/resume`,
      { method: "POST" }
    );
    if (response.ok) { setPhase("sent"); return; }
    setPhase("idle");
    if (response.status === 409) {
      try {
        const body = await response.json() as { reason?: string };
        setRefusal(body.reason === "run_active" ? "run_active" : "not_ready");
      } catch { setRefusal("not_ready"); }
      return;
    }
    setRefusal("error");
  }
  return (
    <div className="training-resume">
      {resumeState === "silent" && (
        <p className="message warning-message">{props.t("staffConsole.trainingRun.resumeSilent")}</p>
      )}
      {phase === "confirm" ? (
        <div className="training-resume-confirm">
          <span>{props.t("staffConsole.trainingRun.resumeConfirm")}</span>
          <div className="button-row">
            <button className="primary-button compact-button" type="button" onClick={() => void send()}>
              {props.t("staffConsole.trainingRun.resumeGo")}
            </button>
            <button className="secondary-button compact-button" type="button" onClick={() => setPhase("idle")}>
              {props.t("staffConsole.trainingRun.resumeBack")}
            </button>
          </div>
        </div>
      ) : (
        <button
          className="primary-button compact-button"
          type="button"
          disabled={phase === "sending"}
          onClick={() => setPhase("confirm")}
        >
          {phase === "sending"
            ? props.t("staffConsole.trainingRun.resuming")
            : props.t("staffConsole.trainingRun.resume")}
        </button>
      )}
      {refusal === "run_active" && <p className="message error-message" role="alert">{props.t("staffConsole.trainingRun.refusalRunActive")}</p>}
      {refusal === "not_ready" && <p className="message error-message" role="alert">{props.t("staffConsole.trainingRun.refusalNotReady")}</p>}
      {refusal === "error" && <p className="message error-message" role="alert">{props.t("staffConsole.error")}</p>}
    </div>
  );
}

export function TrainingRunCard(props: StaffPaneProps) {
  const detail = props.detail;
  const run = detail.trainingRun ?? null;
  const step = detail.steps.find((row) => row.key === "training");
  const stepStatus = step ? staffStepStatus(step) : "locked";
  const trialRow = detail.resources.find((row) => String(row.key) === "trial_link");
  const trial = trialRow ? parseValue(trialRow) : {};
  const hasTrialLink = typeof trial.url === "string" && trial.url.startsWith("https://");
  const sectionState = detail.sections.find((section) => String(section.key) === "training")?.state ?? null;
  const live = Boolean(run && (run.status === "running" || run.status === "waiting_input"));
  const now = useNowTick(live);
  const view = trainingRunView({ sectionState, run, hasTrialLink, now });

  // Everything the canonical status already explains stays with the audited
  // phase card: locked and blocked (done without a bot) keep it verbatim. The
  // live card takes over the moment there is a run to show.
  if (stepStatus === "blocked" || view.state === "locked") {
    return <TrainingPhaseCard {...props} />;
  }

  // Working with no run yet = honest cold start: the working copy the audit
  // approved (a2-01: only the automation state may say Alex works) plus the
  // one place the static "2-3 dny" survives, and a truthful note that the
  // detailed progress appears once the machine reports.
  if (!run || view.state === "queued") {
    return (
      <article className="staff-card training-run-card" data-training-state="queued">
        <header className="training-run-head">
          <WorkingAvatar size="card" />
          <div className="training-run-head-text">
            <h3>{props.t("staffConsole.trainingPhase.title")}</h3>
            <p className="staff-card-hint">{props.t("staffConsole.trainingPhase.running")}</p>
          </div>
        </header>
        <p className="training-run-queued-note">{props.t("staffConsole.trainingRun.queued")}</p>
      </article>
    );
  }

  if (view.state === "done") {
    const total = run.finishedAt
      ? Math.max(0, Math.floor((Date.parse(run.finishedAt) - Date.parse(run.startedAt)) / 1000))
      : null;
    return (
      <>
        <TrainingPhaseCard {...props} />
        <article className="staff-card training-run-card" data-training-state="done">
          <h3>{props.t("staffConsole.trainingRun.timeline")}</h3>
          {total !== null && (
            <p className="staff-card-hint">
              {props.t("staffConsole.trainingRun.doneIn").replace("{duration}", formatSeconds(total))}
            </p>
          )}
          <PhaseLadder live={false} now={now} run={run} t={props.t} />
          <ActivityFeed run={run} t={props.t} />
        </article>
      </>
    );
  }

  const currentRow = run.phases.find((phase) => phase.status === "running")
    ?? run.phases.find((phase) => phase.phase === run.currentPhase)
    ?? null;
  const currentLabelKey = currentRow ? trainingPhaseLabelKey(currentRow.phase) : null;
  const stale = view.state === "stale";
  const silentMinutes = Math.max(1, Math.floor((now - Date.parse(run.updatedAt)) / 60_000));
  const runElapsed = Math.max(0, Math.floor((now - Date.parse(run.startedAt)) / 1000));
  const failedPhase = run.phases.find((phase) => phase.status === "failed") ?? null;
  const failedLabelKey = failedPhase ? trainingPhaseLabelKey(failedPhase.phase) : null;

  return (
    <article className="staff-card training-run-card" data-training-state={view.state}>
      <header className="training-run-head">
        {(view.state === "running" || view.state === "waiting") && <WorkingAvatar size="card" />}
        <div className="training-run-head-text">
          <h3>{props.t("staffConsole.trainingPhase.title")}</h3>
          {(view.state === "running" || view.state === "waiting" || stale) && currentRow && currentLabelKey && (
            <p className="training-run-current">
              {props.t("staffConsole.trainingRun.stepOf")
                .replace("{phase}", String(currentRow.phase))
                .replace("{total}", String(Math.max(TRAINING_PHASE_COUNT, run.phases.length)))
                .replace("{name}", props.t(currentLabelKey))}
            </p>
          )}
        </div>
        <div className="training-run-meta">
          <span>{props.t("staffConsole.trainingRun.runElapsed").replace("{duration}", formatSeconds(runElapsed))}</span>
          <span className={stale ? "training-run-meta-stale" : undefined}>
            {props.t("staffConsole.trainingRun.lastReport").replace("{duration}", formatSeconds((now - Date.parse(run.updatedAt)) / 1000))}
          </span>
          {(detail.trainingRunAttempts ?? 1) > 1 && (
            <span>{props.t("staffConsole.trainingRun.attempt").replace("{count}", String(detail.trainingRunAttempts))}</span>
          )}
        </div>
      </header>

      {stale && (
        <div className="message warning-message training-run-strip" role="alert">
          <strong>{props.t("staffConsole.trainingRun.staleTitle")}</strong>{" "}
          {props.t("staffConsole.trainingRun.staleBody").replace("{minutes}", String(silentMinutes))}{" "}
          {props.t("staffConsole.trainingRun.staleAdvice")}
        </div>
      )}
      {view.state === "failed" && (
        <div className="message error-message training-run-strip" role="alert">
          <strong>{props.t("staffConsole.trainingRun.failedTitle")}</strong>{" "}
          {failedPhase && failedLabelKey && (
            props.t("staffConsole.trainingRun.failedPhase")
              .replace("{phase}", String(failedPhase.phase))
              .replace("{name}", props.t(failedLabelKey))
          )}
          {run.note && <span className="training-run-note"> {run.note}</span>}
        </div>
      )}
      {view.state === "stopped" && (
        <div className="message warning-message training-run-strip" role="alert">
          {props.t("staffConsole.trainingRun.stopped")}
        </div>
      )}
      {view.state === "waiting" && run.waiting && (
        <div className="message training-run-strip training-run-waiting" role="status">
          <strong>{props.t("staffConsole.trainingRun.waitingTitle")}</strong>{" "}
          {run.waiting.message}
          <span className="training-run-note"> {props.t("staffConsole.trainingRun.waitingHint")}</span>
        </div>
      )}

      <ResumeControl {...props} now={now} view={view} />
      <PhaseLadder live={view.state === "running" || view.state === "waiting"} now={now} run={run} t={props.t} />
      <ActivityFeed run={run} t={props.t} />
    </article>
  );
}
